import React, { useState, useEffect } from 'react';
import likeService from '../services/likeService';
import commentService, { Comment } from '../services/commentService';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';

interface PostInteractionsProps {
  postId: string;
  initialLikesCount: number;
  initialCommentsCount: number;
  onLikeUpdate?: (newCount: number) => void;
  onCommentUpdate?: (newCount: number) => void;
}

const PostInteractions: React.FC<PostInteractionsProps> = ({
  postId,
  initialLikesCount,
  initialCommentsCount,
  onLikeUpdate,
  onCommentUpdate,
}) => {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [commentsCount, setCommentsCount] = useState(initialCommentsCount);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [likingPost, setLikingPost] = useState(false);

  // Check if user has liked this post on component mount
  useEffect(() => {
    const checkLikeStatus = async () => {
      try {
        const response = await likeService.checkLikeStatus(postId);
        setIsLiked(response.isLiked);
        setLikesCount(response.likesCount);
      } catch (error) {
        console.error('Error checking like status:', error);
      }
    };

    checkLikeStatus();
  }, [postId]);

  const handleLike = async () => {
    if (likingPost) return;

    try {
      setLikingPost(true);
      const response = await likeService.toggleLike(postId);
      setIsLiked(response.isLiked);
      setLikesCount(response.likesCount);
      onLikeUpdate?.(response.likesCount);
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setLikingPost(false);
    }
  };

  const loadComments = async () => {
    if (loadingComments) return;

    try {
      setLoadingComments(true);
      const response = await commentService.getComments(postId, 1, 20);
      setComments(response.comments);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleShowComments = () => {
    if (!showComments) {
      loadComments();
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;

    try {
      setSubmittingComment(true);
      const response = await commentService.createComment(postId, {
        content: newComment.trim(),
      });

      setComments(prev => [response.comment, ...prev]);
      setCommentsCount(prev => prev + 1);
      setNewComment('');
      onCommentUpdate?.(commentsCount + 1);
    } catch (error) {
      console.error('Error creating comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await commentService.deleteComment(commentId);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
      setCommentsCount(prev => prev - 1);
      onCommentUpdate?.(commentsCount - 1);
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <div>
      {/* Post Actions */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <button
            className={`btn btn-link p-0 me-3 d-flex align-items-center ${isLiked ? 'text-danger' : 'text-grey-500'}`}
            onClick={handleLike}
            disabled={likingPost}
          >
            <i className={`feather-heart font-md me-1 ${isLiked ? 'text-danger' : ''}`}></i>
            <span className="font-xsss">{likesCount}</span>
          </button>

          <button
            className="btn btn-link p-0 me-3 d-flex align-items-center text-grey-500"
            onClick={handleShowComments}
          >
            <i className="feather-message-circle font-md me-1"></i>
            <span className="font-xsss">{commentsCount}</span>
          </button>

          {/* <button className="btn btn-link p-0 text-grey-500">
            <i className="feather-share font-md"></i>
          </button> */}
        </div>

        <button className="btn btn-link p-0 text-grey-500">
          <i className="feather-bookmark font-md"></i>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="border-top pt-3">
          {/* Comments List */}
          {loadingComments ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading comments...</span>
              </div>
            </div>
          ) : (
            <div className="comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className="d-flex align-items-start mb-3">
                  <div className="me-2">
                    <Avatar
                      src={comment.user.avatar}
                      alt={comment.user.username}
                      size="sm"
                    />
                  </div>
                  <div className="flex-grow-1">
                    <div className="bg-light rounded-3 p-2">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <h6 className="fw-600 font-xsss mb-0">{comment.user.username}</h6>
                        <div className="d-flex align-items-center">
                          <span className="text-grey-500 font-xsss me-2">
                            {formatTimeAgo(comment.createdAt)}
                          </span>
                          {comment.userId === user?.id && (
                            <button
                              className="btn btn-link p-0 text-grey-500"
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <i className="feather-trash-2 font-xss"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="font-xsss mb-0">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))}

              {comments.length === 0 && !loadingComments && (
                <div className="text-center py-3">
                  <p className="text-grey-500 font-xsss mb-0">No comments yet. Be the first to comment!</p>
                </div>
              )}
            </div>
          )}

          {/* Comment Input */}
          <form onSubmit={handleSubmitComment} className="mb-3">
            <div className="d-flex align-items-start">
              <div className="me-2">
                <Avatar
                  src={user?.avatar}
                  alt={user?.username || 'User'}
                  size="sm"
                />
              </div>
              <div className="flex-grow-1">
                <div className="position-relative">
                  <textarea
                    className="form-control border-0 bg-light rounded-3 font-xsss"
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={2}
                    disabled={submittingComment}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm position-absolute bottom-0 end-0 m-2"
                    disabled={!newComment.trim() || submittingComment}
                  >
                    {submittingComment ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <i className="feather-send font-xss"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PostInteractions;
